import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useOnboardingSchema,
  useOnboardingData,
  useCreateOnboarding,
  useSaveOnboardingSection,
  useActivateOnboarding,
  useDoctorsList,
} from '@api/queries';
import Button from '@components/ui/Button';
import ConfirmDialog from '@components/ui/ConfirmDialog';
import { DocCard, Lightbox } from '@components/ui/DocUpload';
import docStyles from '@components/ui/DocUpload.module.css';
import FormField from './FormField.jsx';
import styles from './SchemaForm.module.css';
import fieldStyles from './FormField.module.css';

/**
 * SchemaForm
 *
 * Generic, schema-driven onboarding form. Reads the schema for `kind`
 * (doctor / staff / drug), renders sidebar + active section + footer.
 *
 * Save model:
 *   - First section (where schema marks `isCreate: true`) → POST → returns new id
 *   - Subsequent sections → PATCH to /sections/:sectionId
 *
 * Props:
 *   kind     — 'doctor' | 'staff' | 'drug'
 *   id       — existing record id; null/undefined to create a new one
 *   onSaved  — (newId) => void          fires after a successful create
 *   onClose  — () => void               fires when user clicks Back
 */

// Collect simple input fields from a section's blocks (field + gridN).
// Recurses into `subsection` blocks, whose fields live in nested `blocks`
// (e.g. the drug master schema) — otherwise required-field validation would
// silently skip them.
function collectSectionFields(section) {
  const out = [];
  const walk = (blocks) => {
    (blocks || []).forEach((b) => {
      if (b.type === 'field' && b.field) out.push(b.field);
      else if ((b.type === 'grid2' || b.type === 'grid3' || b.type === 'grid4') && Array.isArray(b.fields)) out.push(...b.fields);
      else if (b.type === 'subsection' && Array.isArray(b.blocks)) walk(b.blocks);
    });
  };
  walk(section?.blocks);
  return out;
}

// Validate phone/email/number formats, and (only when requireAll) required
// fields, before saving or advancing a section.
//
// `requireAll` is false for the per-section "Save & continue" (progress save):
// the form literally "saves progress as you go", and the backend never blanks a
// saved value from an empty field, so a half-filled section must be allowed to
// save — otherwise editing a doctor whose earlier data is incomplete gets stuck
// and the edit silently doesn't persist. Required-field completeness is enforced
// only at the final "Complete & Activate" step (requireAll = true).
function validateSection(section, values, { requireAll = true } = {}) {
  const errs = {};
  const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  collectSectionFields(section).forEach((f) => {
    const v = values[f.key];
    const empty = v == null || v === '' || (Array.isArray(v) && v.length === 0);
    if (empty) { if (requireAll && f.required) errs[f.key] = 'This field is required'; return; }
    const telMax = f.maxLength ?? 10;
    if (f.type === 'tel' && String(v).length !== telMax) errs[f.key] = `Enter a ${telMax}-digit number`;
    else if (f.type === 'email' && !emailRe.test(String(v))) errs[f.key] = 'Enter a valid email';
    else if (f.type === 'number' && f.min != null && Number(v) < Number(f.min)) errs[f.key] = `Must be ${f.min} or more`;
  });
  return errs;
}

/* Map a section (by its title / id keywords) to an Ayurvedic illustration in
   /public/forms and a Bootstrap-icon fallback. Unmatched sections show the icon
   only — so missing images degrade gracefully. */
const SECTION_ART = [
  { k: /identit|clinic\s*detail/i,                 img: 'identity',     icon: 'hospital' },
  { k: /personal/i,                                img: null,           icon: 'person-vcard' },
  { k: /contact|communicat|reach/i,                img: 'contact',      icon: 'telephone' },
  { k: /availab|hour|timing|schedule/i,            img: 'availability', icon: 'calendar-check' },
  { k: /facilit|room|capacity|infrastructure/i,    img: 'facilities',   icon: 'hospital' },
  { k: /service|treatment/i,                       img: 'services',     icon: 'flower1' },
  { k: /team|clinical|specialt/i,                  img: 'team',         icon: 'people' },
  { k: /experience|professional/i,                 img: 'team',         icon: 'briefcase' },
  { k: /complian|licen|registration/i,             img: 'compliance',   icon: 'patch-check' },
  { k: /financ|bank|payment|charge|\btax\b/i,      img: 'finance',      icon: 'cash-stack' },
  { k: /digital|social|online|presence|media/i,    img: 'digital',      icon: 'globe2' },
  { k: /practition|profile/i,                      img: 'practitioner', icon: 'person-badge' },
  { k: /educat|qualif|degree|college|school/i,     img: 'education',    icon: 'mortarboard' },
  { k: /document|upload|proof/i,                   img: 'documents',    icon: 'folder' },
  { k: /permission|system|access/i,                img: null,           icon: 'gear' },
  { k: /interest|engagement|participation/i,       img: null,           icon: 'stars' },
];
function sectionArt(section) {
  const hay = `${section?.title || ''} ${section?.id || ''}`;
  const m = SECTION_ART.find((e) => e.k.test(hay));
  return {
    src: m && m.img ? `/forms/form-${m.img}.webp` : null,
    icon: m ? m.icon : 'file-earmark-text',
  };
}

/* Premium section header — illustration + "SECTION 0X" eyebrow + title + sub. */
function SectionHeader({ section, action, eyebrow }) {
  const art = sectionArt(section);
  const [imgOk, setImgOk] = useState(true);
  const num = String(section?.num ?? '').padStart(2, '0');
  // The stepper dot already carries the step number; the eyebrow shows the
  // group / phase instead (real context, not a duplicate number). Falls back
  // to "Section 0X" for ungrouped schemas.
  const eb = eyebrow || `Section ${num}`;
  return (
    <div className={styles.secHead}>
      <div className={styles.secArt}>
        {art.src && imgOk ? (
          <img src={art.src} alt="" className={styles.secImg} loading="lazy" onError={() => setImgOk(false)} />
        ) : (
          <div className={styles.secIconWrap}><i className={`bi bi-${art.icon}`} aria-hidden="true" /></div>
        )}
      </div>
      <div className={styles.secMeta}>
        <div className={styles.secEyebrow}>{eb}</div>
        <div className={styles.secTitle}>{section?.title}</div>
        {section?.subtitle && <div className={styles.secSub}>{section.subtitle}</div>}
      </div>
      {action && <div className={styles.secAction}>{action}</div>}
    </div>
  );
}

/* Lightweight skeleton of the onboarding shell — shown while the schema /
   record loads, matching the real stepper + header + fields so there's no
   layout jump (consistent with the list screens' skeleton loaders). */
function FormSkeleton() {
  return (
    <div className={styles.shell} aria-busy="true" aria-label="Loading form">
      <aside className={styles.sidebar}>
        <div className={`${styles.sk} ${styles.skHead}`} />
        <div className={`${styles.sk} ${styles.skProgress}`} />
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className={styles.skStep}>
            <div className={`${styles.sk} ${styles.skDot}`} />
            <div className={`${styles.sk} ${styles.skStepLabel}`} />
          </div>
        ))}
      </aside>
      <div className={styles.main}>
        <div className={styles.head}>
          <div style={{ flex: 1 }}>
            <div className={`${styles.sk} ${styles.skTitle}`} />
            <div className={`${styles.sk} ${styles.skSub}`} />
          </div>
        </div>
        <div className={styles.scroller}>
          <div className={styles.secHead}>
            <div className={`${styles.sk} ${styles.skArt}`} />
            <div style={{ flex: 1 }}>
              <div className={`${styles.sk} ${styles.skEyebrow}`} />
              <div className={`${styles.sk} ${styles.skSecTitle}`} />
            </div>
          </div>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className={styles.skField}>
              <div className={`${styles.sk} ${styles.skFieldLabel}`} />
              <div className={`${styles.sk} ${styles.skInput}`} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function SchemaForm({ kind, id, onSaved, onClose, scope, viewMode = false, skipSections = [] }) {
  const { data: schema, isLoading: schemaLoading } = useOnboardingSchema(kind);
  const { data: record,  isLoading: dataLoading }   = useOnboardingData(kind, id);

  // Local in-progress id for newly-created records (so the same component
  // instance keeps editing after the first POST)
  const [createdId, setCreatedId] = useState(null);
  const effectiveId = id || createdId;

  /* `scope` carries clinic-scoped creation (Clinic Panel, B54): the new
     doctor / staff / patient joins that clinic's masters, not the network. */
  const createMut = useCreateOnboarding(kind, scope);
  const saveMut   = useSaveOnboardingSection(kind, effectiveId);
  const activateMut = useActivateOnboarding(kind, effectiveId);
  const canActivate = effectiveId && (kind === 'doctor' || kind === 'staff' || kind === 'clinic');

  const [activeIdx, setActiveIdx] = useState(0);
  const [values, setValues] = useState({});      // per-section working values
  const [touched, setTouched] = useState(false); // has the user edited this section?
  const [errorMsg, setErrorMsg] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [justSaved, setJustSaved] = useState(false);
  // In-app confirm modal (replaces window.confirm): { title, message, confirmLabel, danger, onConfirm }
  const [confirm, setConfirm] = useState(null);
  // View-first: existing records open read-only; "Edit" unlocks the form.
  const [editing, setEditing] = useState(!viewMode);

  // Visible sections — the onboarding tabs hide document/permission sections
  // collected elsewhere (the KYC Documents tab), so they never duplicate.
  const sections = (schema?.sections || []).filter((s) => !skipSections.includes(s.id));

  // When schema or record loads, hydrate the active section's values
  useEffect(() => {
    if (!schema) return;
    const section = sections[activeIdx];
    if (!section) return;
    setValues(record?.[section.id] || {});
    setTouched(false);
    setErrorMsg(null);
  }, [schema, record, activeIdx]);

  /* ──────────────────────────── Derived ──────────────────────────── */

  const activeSection = sections[activeIdx];

  const completed = useMemo(() => {
    const list = record?._meta?.completedSections || [];
    return new Set(list);
  }, [record]);

  const progressPct = record?._meta?.progressPct ?? 0;
  const completedCount = sections.filter((s) => completed.has(s.id)).length;

  /* visibleWhen: a section may depend on another section's saved value
     (or the live value if that section is the one being edited).
     e.g. Menstrual History appears only when gender = Female. */
  const sectionVisible = useCallback((sec) => {
    const vw = sec?.visibleWhen;
    if (!vw) return true;
    const live = activeSection?.id === vw.section ? values[vw.field] : undefined;
    const saved = record?.[vw.section]?.[vw.field];
    return (live ?? saved) === vw.equals;
  }, [activeSection, values, record]);

  // Schema groups → sidebar grouping
  const groupedSections = useMemo(() => {
    if (!schema) return [];
    if (!schema.groups || schema.groups.length === 0) {
      // No groups defined: show all sections under one unlabeled group
      return [{ label: null, sections }];
    }
    return schema.groups.map((g) => ({
      label: g.label,
      sections: g.sections
        .map((sid) => sections.find((s) => s.id === sid))
        .filter(Boolean),
    })).filter((grp) => grp.sections.length > 0);
  }, [schema, sections, sectionVisible]);

  /* ──────────────────────────── Handlers ──────────────────────────── */

  const setFieldValue = useCallback((key, val) => {
    setValues((prev) => ({ ...prev, [key]: val }));
    setTouched(true);
    setJustSaved(false);
  }, []);

  const goToSection = useCallback((idx) => {
    setActiveIdx(Math.max(0, Math.min(sections.length - 1, idx)));
  }, [sections.length]);

  const handleSave = useCallback(async ({ requireAll = false } = {}) => {
    if (!activeSection) return false;
    setErrorMsg(null);
    setJustSaved(false);

    const verrs = validateSection(activeSection, values, { requireAll });
    if (Object.keys(verrs).length) {
      setFieldErrors(verrs);
      setErrorMsg('Please fix the highlighted fields.');
      return false;
    }
    setFieldErrors({});

    // Decide POST vs PATCH:
    // - The first section is marked `isCreate: true` in the schema.
    // - If we don't have an id yet (creating a new record), use POST.
    // - Otherwise PATCH.
    try {
      if (!effectiveId && activeSection.isCreate) {
        const result = await createMut.mutateAsync({
          sectionId: activeSection.id,
          values,
        });
        setCreatedId(result.id);
        if (onSaved) onSaved(result.id);
      } else if (effectiveId) {
        await saveMut.mutateAsync({
          sectionId: activeSection.id,
          values,
        });
      } else {
        // Sections beyond the first cannot be saved before the create section
        setErrorMsg('Please complete the first section first.');
        return false;
      }
      setTouched(false);
      setJustSaved(true);
      return true;
    } catch (err) {
      setErrorMsg(err?.message || 'Could not save. Try again.');
      return false;
    }
  }, [activeSection, effectiveId, values, createMut, saveMut, onSaved]);

  const handleSaveAndNext = useCallback(async () => {
    // Creating a new record: required fields must be complete before we create
    // it / advance (QA STAFF-007, MF-013, PROC_009, CLINIC-009). Editing an
    // existing record stays lenient so partial progress can still be saved
    // (a record whose earlier data is incomplete must not get stuck).
    const ok = await handleSave({ requireAll: !effectiveId });
    // Only advance when the save actually succeeded — otherwise the user
    // silently skips past a failed section and thinks nothing happened.
    if (ok && activeIdx < sections.length - 1) {
      setActiveIdx((i) => i + 1);
    }
  }, [handleSave, effectiveId, activeIdx, sections.length]);

  // "Complete & Activate" — save the last section, then flip the record's
  // status to Active. Only supported for kinds that have an activate endpoint
  // (doctor, staff). Falls back to plain save otherwise.
  const handleComplete = useCallback(async () => {
    // Final step: now the whole section must be complete, not just format-valid.
    const ok = await handleSave({ requireAll: true });
    if (!ok) return;
    if (canActivate) {
      try {
        await activateMut.mutateAsync();
      } catch (err) {
        setErrorMsg(err?.message || 'Saved, but could not activate. Please try again.');
        return;
      }
    }
    if (onClose) onClose();
  }, [handleSave, canActivate, activateMut, onClose]);

  // Guard against losing typed-but-unsaved edits when leaving the form.
  // A record is only ever created/updated by an explicit Save, so leaving must
  // never save silently — but we do warn first (QA CLINIC-020).
  const hasUnsaved = touched && !justSaved;
  useEffect(() => {
    if (!hasUnsaved) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasUnsaved]);

  const guardedClose = useCallback(() => {
    if (hasUnsaved) {
      setConfirm({
        title: 'Leave without saving?',
        message: 'You have unsaved changes on this section. They will be lost if you leave now.',
        confirmLabel: 'Leave',
        danger: true,
        onConfirm: () => { if (onClose) onClose(); },
      });
      return;
    }
    if (onClose) onClose();
  }, [hasUnsaved, onClose]);

  const isSaving = createMut.isPending || saveMut.isPending;

  // Keep the subtitle's "N sections" count honest with the real section list
  // (some schemas shipped a stale hard-coded number, e.g. clinic said "10").
  const headSubtitle = typeof schema?.subtitle === 'string'
    ? schema.subtitle.replace(/\b\d+\s+sections\b/i, `${sections.length} sections`)
    : schema?.subtitle;

  /* ──────────────────────────── Render ──────────────────────────── */

  if (schemaLoading || (id && dataLoading)) {
    return <FormSkeleton />;
  }
  if (!schema) {
    return <div className={styles.loadingBlock}>Could not load form schema.</div>;
  }
  if (!activeSection) {
    return <div className={styles.loadingBlock}>This form has no sections configured yet.</div>;
  }

  return (
    <div className={styles.shell}>
      {/* ── Sidebar ────────────────────────────────────────────────── */}
      <aside className={styles.sidebar}>
        <div className={styles.progressHead}>Onboarding progress</div>
        <div className={styles.progressBar}>
          <div className={styles.progressFill} style={{ width: `${progressPct}%` }} />
        </div>
        <div className={styles.progressCount}>
          <span>{completedCount} of {sections.length} sections</span>
          <span className={styles.progressPct}>{progressPct}%</span>
        </div>

        {groupedSections.map((g, gi) => (
          <div key={gi} className={styles.group}>
            {g.label && <div className={styles.groupLabel}>{g.label}</div>}
            <div className={styles.steps}>
              {g.sections.map((sec, si) => {
                const idx = sections.indexOf(sec);
                const isActive = idx === activeIdx;
                const isDone = completed.has(sec.id);
                return (
                  <button
                    key={`${gi}-${si}`}
                    type="button"
                    aria-current={isActive ? 'step' : undefined}
                    className={`${styles.navItem} ${isActive ? styles.navItemActive : ''} ${isDone ? styles.navItemDone : ''}`}
                    onClick={() => goToSection(idx)}
                  >
                    <span className={`${styles.navCheck} ${isDone ? styles.navCheckDone : ''} ${isActive ? styles.navCheckActive : ''}`}>
                      {isDone
                        ? <i className="bi bi-check-lg" aria-hidden="true" />
                        : <span className={styles.navNum}>{sec.num}</span>}
                    </span>
                    <span className={styles.navLabel}>{sec.title}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </aside>

      {/* ── Main pane ──────────────────────────────────────────────── */}
      <div className={styles.main}>
        <div className={styles.head}>
          <div>
            <div className={styles.headTitle}>{schema.title}</div>
            <div className={styles.headSub}>{headSubtitle}</div>
          </div>
          {effectiveId && <span className={styles.headBadge}>{effectiveId}</span>}
          {!editing && effectiveId && (
            <Button variant="pu" size="sm" onClick={() => setEditing(true)}><i className="bi bi-pencil" aria-hidden="true" /> Edit</Button>
          )}
          {editing && canActivate && (
            <Button
              variant="dk"
              size="sm"
              disabled={activateMut.isPending}
              onClick={() => {
                // Don't silently swallow typed-but-unsaved edits on activate —
                // ask first (QA STAFF-008). Activation only flips status; it does
                // not save the current field edits.
                const run = async () => {
                  try {
                    await activateMut.mutateAsync();
                    if (onClose) onClose();
                  } catch (err) {
                    setErrorMsg(err?.message || 'Could not activate.');
                  }
                };
                if (hasUnsaved) {
                  setConfirm({
                    title: 'Activate without saving?',
                    message: 'You have unsaved changes that will NOT be saved. Activation only changes the status.',
                    confirmLabel: 'Activate anyway',
                    danger: false,
                    onConfirm: run,
                  });
                  return;
                }
                run();
              }}
            >
              {activateMut.isPending ? 'Activating…' : 'Activate now'}
            </Button>
          )}
        </div>

        {!editing && (
          <div className={styles.errorBanner} style={{ background: 'var(--info-l)', color: 'var(--info)', border: '1px solid var(--info-m)' }}>
            Viewing this record. Click <strong><i className="bi bi-pencil" aria-hidden="true" /> Edit</strong> to make changes.
          </div>
        )}

        <div className={styles.scroller}>
          <SectionHeader
            key={`head-${activeSection.id}`}
            section={activeSection}
            eyebrow={groupedSections.find((g) => g.sections.some((s) => s.id === activeSection.id))?.label}
            action={
              /* Optional sections (e.g. Clinic Admin assignment) can be skipped
                 straight to the next step without filling anything. */
              editing && activeSection.optional && activeIdx < sections.length - 1 ? (
                <Button
                  variant="se"
                  size="xs"
                  onClick={() => { setFieldErrors({}); setErrorMsg(''); goToSection(activeIdx + 1); }}
                >
                  Skip this step <i className="bi bi-arrow-right" aria-hidden="true" />
                </Button>
              ) : null
            }
          />

          {errorMsg && <div className={styles.errorBanner}>{errorMsg}</div>}

          <div
            key={`body-${activeSection.id}`}
            className={styles.sectionBody}
            style={editing ? undefined : { pointerEvents: 'none', opacity: 0.9 }}
            aria-readonly={!editing}
          >
            {activeSection.blocks.map((block, bi) => (
              <BlockRenderer
                errors={fieldErrors}
                key={bi}
                block={block}
                values={values}
                setFieldValue={setFieldValue}
                sectionValues={values}
                uploadCtx={{ kind, clinicId: kind === 'clinic' ? effectiveId : null }}
              />
            ))}
          </div>

          {/* ── Footer ──────────────────────────────────────────────── */}
          <div className={styles.footer}>
            <Button variant="se" size="sm" onClick={guardedClose}><i className="bi bi-arrow-left" aria-hidden="true" /> Back to list</Button>
            <div className={styles.footerSpacer} />

            {isSaving && <span className={styles.savingNote}>Saving…</span>}
            {!isSaving && justSaved && <span className={styles.savedNote}><i className="bi bi-check-circle" aria-hidden="true" /> Saved</span>}

            <Button
              variant="se"
              size="sm"
              onClick={() => goToSection(activeIdx - 1)}
              disabled={activeIdx === 0}
            >
              <i className="bi bi-arrow-left" aria-hidden="true" /> Previous
            </Button>

            {!editing ? (
              <Button
                variant="pu"
                size="sm"
                onClick={() => {
                  // Read-only view: fields can't be edited, so never trap the
                  // user behind a validation error they have no way to fix.
                  setFieldErrors({});
                  goToSection(activeIdx + 1);
                }}
                disabled={activeIdx === sections.length - 1}
              >
                Next <i className="bi bi-arrow-right" aria-hidden="true" />
              </Button>
            ) : activeIdx < sections.length - 1 ? (
              <Button variant="pu" size="sm" onClick={handleSaveAndNext} disabled={isSaving}>
                Save & continue <i className="bi bi-arrow-right" aria-hidden="true" />
              </Button>
            ) : (
              <Button variant="dk" size="sm" onClick={handleComplete} disabled={isSaving || activateMut.isPending}>
                {activateMut.isPending ? 'Activating…' : (canActivate ? 'Complete & Activate' : 'Complete onboarding')}
              </Button>
            )}
          </div>
        </div>
      </div>

      {confirm && (
        <ConfirmDialog
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          cancelLabel="Cancel"
          danger={confirm.danger}
          onConfirm={() => { const fn = confirm.onConfirm; setConfirm(null); fn?.(); }}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   BlockRenderer — dispatches to the right block type
   ════════════════════════════════════════════════════════════════════ */

function BlockRenderer({ block, values, setFieldValue, uploadCtx, errors }) {
  switch (block.type) {
    case 'field':
      return (
        <div className={fieldStyles.row}>
          <FormField
            field={block.field}
            value={values[block.field.key]}
            onChange={(v) => setFieldValue(block.field.key, v)}
            error={errors?.[block.field.key]}
            uploadCtx={uploadCtx}
          />
        </div>
      );

    case 'grid2':
    case 'grid3':
    case 'grid4': {
      const gridCls = fieldStyles[block.type];
      return (
        <div className={`${gridCls} ${fieldStyles.row}`}>
          {block.fields.map((f) => (
            <FormField
              key={f.key}
              field={f}
              value={values[f.key]}
              onChange={(v) => setFieldValue(f.key, v)}
              error={errors?.[f.key]}
              uploadCtx={uploadCtx}
            />
          ))}
        </div>
      );
    }

    case 'subsection':
      // A subsection is a titled group whose input fields live in nested
      // `blocks`. The old renderer showed only the title, so those fields
      // (e.g. every drug-master field) never appeared — cause of DRUG_011/012.
      return (
        <>
          {(block.title || block.label) && (
            <div className={fieldStyles.subsection}>{block.title || block.label}</div>
          )}
          {(block.blocks || []).map((child, ci) => (
            <BlockRenderer
              key={ci}
              block={child}
              values={values}
              setFieldValue={setFieldValue}
              uploadCtx={uploadCtx}
              errors={errors}
            />
          ))}
        </>
      );

    case 'info-text':
      return <div className={fieldStyles.infoText}>{block.text}</div>;

    case 'info-banner':
      return <div className={fieldStyles.infoBanner}>{block.text}</div>;

    case 'table':
      return <TableBlock block={block} values={values} setFieldValue={setFieldValue} />;

    case 'upload-table':
      return <UploadTableBlock block={block} values={values} setFieldValue={setFieldValue} />;

    case 'declaration':
      return <DeclarationBlock block={block} values={values} setFieldValue={setFieldValue} />;

    case 'doctor-picker':
      return <DoctorPickerBlock block={block} values={values} setFieldValue={setFieldValue} sectionValues={values} />;

    case 'permissions-matrix':
      return <PermissionsBlock block={block} values={values} setFieldValue={setFieldValue} />;

    default:
      return (
        <div className={fieldStyles.infoText}>
          Block type "{block.type}" not yet supported in renderer
        </div>
      );
  }
}

/* ──────────────────────────── Block: table ──────────────────────────── */

function TableBlock({ block, values, setFieldValue }) {
  const rows = Array.isArray(values[block.key]) ? values[block.key] : [];
  const colCount = block.columns.length;
  // Give every column a usable minimum width so headings never crush or clip;
  // the block scrolls sideways if the total is wider than the card.
  const gridTemplate = `repeat(${colCount}, minmax(132px, 1fr)) 44px`;
  const isTimings = block.key === 'timings';

  const addRow = () => {
    const blank = {};
    block.columns.forEach((c) => { blank[c.key] = ''; });
    setFieldValue(block.key, [...rows, blank]);
  };

  const updateRow = (idx, key, val) => {
    const next = rows.map((r, i) => (i === idx ? { ...r, [key]: val } : r));
    setFieldValue(block.key, next);
  };

  const removeRow = (idx) => {
    setFieldValue(block.key, rows.filter((_, i) => i !== idx));
  };

  // "Same timing for every day": copy the first row's values — except the day
  // itself (the first column) — into every other row.
  const copyFirstToAll = () => {
    if (rows.length < 2) return;
    const src = rows[0];
    const idCol = block.columns[0].key;
    const next = rows.map((r, i) => {
      if (i === 0) return r;
      const merged = { ...r };
      block.columns.forEach((c) => { if (c.key !== idCol) merged[c.key] = src[c.key] ?? ''; });
      return merged;
    });
    setFieldValue(block.key, next);
  };

  return (
    <div className={fieldStyles.tableBlock}>
      <div className={fieldStyles.tableHeader} style={{ gridTemplateColumns: gridTemplate }}>
        {block.columns.map((c) => <div key={c.key}>{c.label}</div>)}
        <div />
      </div>
      {rows.map((row, idx) => (
        <div
          key={idx}
          className={fieldStyles.tableRow}
          style={{ gridTemplateColumns: gridTemplate }}
        >
          {block.columns.map((c) => (
            c.type === 'select' && Array.isArray(c.options) ? (
              <select
                key={c.key}
                value={row[c.key] ?? ''}
                onChange={(e) => updateRow(idx, c.key, e.target.value)}
              >
                <option value="">{c.label}…</option>
                {c.options.map((o) => {
                  const val = typeof o === 'object' ? (o.value ?? o.label) : o;
                  const lbl = typeof o === 'object' ? (o.label ?? o.value) : o;
                  return <option key={val} value={val}>{lbl}</option>;
                })}
              </select>
            ) : (
              <input
                key={c.key}
                // CLINIC-018: a "time" column renders the native time picker
                // instead of a free-text box, so clinic timings are chosen, not typed.
                type={c.type === 'number' ? 'number' : c.type === 'time' ? 'time' : 'text'}
                value={row[c.key] ?? ''}
                onChange={(e) => updateRow(idx, c.key, e.target.value)}
                placeholder={c.label}
              />
            )
          ))}
          <button
            type="button"
            className={fieldStyles.iconBtn}
            onClick={() => removeRow(idx)}
            aria-label="Delete this row"
            title="Delete this row"
          >
            <i className="bi bi-trash" aria-hidden="true" />
          </button>
        </div>
      ))}
      <div className={fieldStyles.tableActions}>
        <Button variant="se" size="xs" onClick={addRow}>+ Add row</Button>
        {isTimings && rows.length >= 2 && (
          <Button variant="gh" size="xs" onClick={copyFirstToAll}>Copy 1st day’s timing to all</Button>
        )}
      </div>
    </div>
  );
}

/* ──────────────────────────── Block: upload-table ──────────────────────────── */

function UploadTableBlock({ block, values, setFieldValue }) {
  const docs = values[block.key] || {};
  const [preview, setPreview] = useState(null);   // doc shown in the lightbox

  // Store (or clear) one document; the DocCard handles its own upload + errors.
  const setDoc = (key, val) => {
    const next = { ...docs };
    if (val) next[key] = val; else delete next[key];
    setFieldValue(block.key, next);
  };

  return (
    <>
      <div className={docStyles.grid}>
        {block.rows.map((row) => (
          <DocCard
            key={row.key}
            label={row.label}
            required={row.required}
            value={docs[row.key]}
            onChange={(val) => setDoc(row.key, val)}
            onView={(f) => setPreview(f)}
          />
        ))}
      </div>
      <Lightbox file={preview} onClose={() => setPreview(null)} />
    </>
  );
}

/* ──────────────────────────── Block: declaration ──────────────────────────── */

function DeclarationBlock({ block, values, setFieldValue }) {
  const accepted = !!values[block.key];
  return (
    <div className={fieldStyles.declaration}>
      {block.text}
      <label className={fieldStyles.declarationCheck}>
        <input
          type="checkbox"
          checked={accepted}
          onChange={(e) => setFieldValue(block.key, e.target.checked)}
        />
        <span>I accept and agree to the above</span>
      </label>
    </div>
  );
}

/* ──────────────────────────── Block: doctor-picker ──────────────────────────── */

/**
 * Doctor picker — used by Clinic onboarding to assign a Clinic Admin.
 *
 * Lazy-loads the doctor list when the block is rendered. Filters to active
 * doctors only. When `filterByClinic` is true and the parent form has a
 * clinic context, restricts to doctors of that clinic.
 *
 * Stores the selected doctor's id (e.g. "MUM12834") in the field.
 */
function DoctorPickerBlock({ block, values, setFieldValue }) {
  const selectedId = values[block.key] || '';

  // Show EVERY doctor, not just Active ones: during clinic onboarding the
  // doctor being assigned as Clinic Admin is often still Pending/onboarding, so
  // an Active-only filter hid them and left the picker nearly empty (QA
  // CLINIC-026 — "only 2 doctors visible but more exist"). No clinic filter yet
  // because the onboarding context doesn't expose a clean clinic id.
  const { data, isLoading, isError } = useDoctorsList({});

  const doctors = data ? data.pages.flatMap((p) => p.doctors) : [];

  const handleChange = (e) => setFieldValue(block.key, e.target.value);

  return (
    <div className={fieldStyles.fg}>
      <label className={`${fieldStyles.fl} ${fieldStyles.flReq}`}>{block.label || 'Doctor'}</label>
      {isLoading ? (
        <div className={fieldStyles.infoText}>Loading doctors…</div>
      ) : isError ? (
        <div className={fieldStyles.infoText} style={{ color: 'var(--critical)' }}>
          Could not load doctors. Try again.
        </div>
      ) : (
        <select
          className={fieldStyles.fs}
          value={selectedId}
          onChange={handleChange}
        >
          <option value="">— Select a doctor —</option>
          {doctors.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name} · {d.qualification || 'BAMS'} · {d.clinic} ({d.id})
            </option>
          ))}
        </select>
      )}
      {block.helperText && (
        <div className={fieldStyles.infoText} style={{ marginTop: 4, marginBottom: 0 }}>
          {block.helperText}
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────── Block: permissions-matrix ──────────────────────────── */

function PermissionsBlock({ block, values, setFieldValue }) {
  const perms = values[block.key] || {};
  const actions = ['Observe', 'Edit', 'Delete'];

  const toggle = (mod, action) => {
    const current = perms[mod] || [];
    const next = current.includes(action)
      ? current.filter((a) => a !== action)
      : [...current, action];
    setFieldValue(block.key, { ...perms, [mod]: next });
  };


  
  return (
    <div className={fieldStyles.permTable}>
      <div className={fieldStyles.permRow}>
        <div className={fieldStyles.permHeader}>Module</div>
        {actions.map((a) => <div key={a} className={fieldStyles.permHeader}>{a}</div>)}
      </div>
      {block.modules.map((mod) => (
        <div key={mod} className={fieldStyles.permRow}>
          <div>{mod}</div>
          {actions.map((a) => (
            <div key={a} className={fieldStyles.permCheck}>
              <input
                type="checkbox"
                checked={(perms[mod] || []).includes(a)}
                onChange={() => toggle(mod, a)}
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}